import React, { useState, useCallback } from "react";
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  VStack,
  HStack,
  Alert,
  AlertIcon,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Select,
  Text,
  Flex,
  useDisclosure,
  IconButton,
  Spinner,
} from "@chakra-ui/react";
import { EditIcon, AddIcon, DeleteIcon } from "@chakra-ui/icons";
import { useAdminData } from "../../hooks/useAdminData";
import { createToast } from "../../utils/toastUtils";
import { formatDate } from "../../utils/dateUtils";
import { Api } from "../../Api";
import { getAllOrganizations } from "../../services/adminService";
import { usePersistentState } from "../../hooks/usePersistentState";

const DeviceManagement = () => {
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [filterOrgId, setFilterOrgId] = usePersistentState(
    "admin:deviceManagement:filterOrgId",
    ""
  );
  const [formData, setFormData] = useState({
    device_id: "",
    organization_id: "",
    description: "",
  });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const toastHelper = createToast(toast);

  const fetchDevices = useCallback(() => {
    const params = new URLSearchParams();
    if (filterOrgId) params.append("organization_id", filterOrgId);
    return Api.get(`/admin/devices?${params.toString()}`).then((r) => r.data);
  }, [filterOrgId]);

  const fetchOrganizations = useCallback(() => getAllOrganizations(), []);

  const {
    data: devicesData,
    loading: devicesLoading,
    error: devicesError,
    refetch: refetchDevices,
  } = useAdminData(fetchDevices, [filterOrgId]);

  const {
    data: organizationsData,
    loading: orgsLoading,
    error: orgsError,
  } = useAdminData(fetchOrganizations, []);

  const devices = devicesData?.devices || [];
  const organizations = organizationsData?.organizations || [];
  const loading = devicesLoading || orgsLoading;
  const error = devicesError || orgsError;

  const handleCreateDevice = () => {
    setSelectedDevice(null);
    setFormData({ device_id: "", organization_id: "", description: "" });
    onOpen();
  };

  const handleEditDevice = (device) => {
    setSelectedDevice(device);
    setFormData({
      device_id: device.device_id,
      organization_id: device.organization_id || "",
      description: device.description || "",
    });
    onOpen();
  };

  const handleSubmit = async () => {
    try {
      if (selectedDevice) {
        await Api.put(`/admin/devices/${selectedDevice.device_id}`, {
          organization_id: parseInt(formData.organization_id) || null,
          description: formData.description,
        });
        toastHelper.success("Device updated successfully");
      } else {
        await Api.post("/admin/devices", {
          device_id: formData.device_id,
          organization_id: parseInt(formData.organization_id) || null,
          description: formData.description,
        });
        toastHelper.success("Device created successfully");
      }
      refetchDevices();
      onClose();
    } catch (err) {
      toastHelper.error(err.response?.data?.detail || "Failed to save device");
    }
  };

  const handleDeleteDevice = async (deviceId) => {
    if (!window.confirm(`Are you sure you want to delete device ${deviceId}? This will also delete all its sessions.`)) return;
    try {
      await Api.delete(`/admin/devices/${deviceId}`);
      toastHelper.success("Device deleted successfully");
      refetchDevices();
    } catch (err) {
      toastHelper.error(err.response?.data?.detail || "Failed to delete device");
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <Spinner size="lg" color="blue.500" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="lg">
        <AlertIcon />
        {error?.message || "Failed to load devices"}
      </Alert>
    );
  }

  return (
    <VStack spacing={5} align="stretch">
      {/* Toolbar */}
      <Flex justify="space-between" align="flex-end" gap={4} wrap="wrap">
        <FormControl maxW="280px">
          <FormLabel fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" mb={1}>
            Organization
          </FormLabel>
          <Select
            size="sm"
            value={filterOrgId}
            onChange={(e) => setFilterOrgId(e.target.value)}
            placeholder="All organizations"
            borderRadius="md"
            bg="white"
          >
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </Select>
        </FormControl>

        <Button
          leftIcon={<AddIcon boxSize={3} />}
          colorScheme="blue"
          size="sm"
          px={5}
          borderRadius="md"
          fontWeight="semibold"
          onClick={handleCreateDevice}
        >
          Register Device
        </Button>
      </Flex>

      {/* Table */}
      {devices.length === 0 ? (
        <Box
          py={16} textAlign="center" bg="gray.50" borderRadius="lg"
          borderWidth="1px" borderColor="gray.200" borderStyle="dashed"
        >
          <Text color="gray.400" fontSize="sm">No devices found</Text>
        </Box>
      ) : (
        <Box overflowX="auto" borderRadius="lg" borderWidth="1px" borderColor="gray.200" bg="white">
          <Table variant="unstyled" size="sm">
            <Thead>
              <Tr bg="gray.50" borderBottomWidth="1px" borderBottomColor="gray.200">
                {["Device ID", "Organization", "Current User", "Description", "Created", ""].map((h) => (
                  <Th key={h} py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">{h}</Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {devices.map((device, idx) => (
                <Tr
                  key={device.id}
                  borderBottomWidth={idx < devices.length - 1 ? "1px" : "0"}
                  borderBottomColor="gray.100"
                  _hover={{ bg: "gray.50" }}
                  transition="background 0.1s"
                >
                  <Td py={3} px={4}>
                    <Text fontSize="sm" fontWeight="semibold" fontFamily="mono">{device.device_id}</Text>
                  </Td>
                  <Td py={3} px={4}>
                    {device.organization_name ? (
                      <Badge colorScheme="blue" variant="subtle" borderRadius="full" px={2} fontSize="xs">{device.organization_name}</Badge>
                    ) : (
                      <Badge colorScheme="gray" variant="subtle" borderRadius="full" px={2} fontSize="xs">Unassigned</Badge>
                    )}
                  </Td>
                  <Td py={3} px={4}>
                    <Text fontSize="sm" color={device.user_email ? "gray.700" : "gray.400"}>
                      {device.user_email || "No user"}
                    </Text>
                  </Td>
                  <Td py={3} px={4} maxW="200px">
                    <Text fontSize="sm" color="gray.600" isTruncated>
                      {device.description || <Text as="span" color="gray.400">—</Text>}
                    </Text>
                  </Td>
                  <Td py={3} px={4}>
                    <Text fontSize="xs" color="gray.400">{formatDate(device.created_at)}</Text>
                  </Td>
                  <Td py={3} px={4} textAlign="right">
                    <HStack spacing={1} justify="flex-end">
                      <IconButton
                        icon={<EditIcon />} size="xs" colorScheme="blue" variant="ghost"
                        borderRadius="md" aria-label="Edit device"
                        onClick={() => handleEditDevice(device)}
                      />
                      <IconButton
                        icon={<DeleteIcon />} size="xs" colorScheme="red" variant="ghost"
                        borderRadius="md" aria-label="Delete device"
                        onClick={() => handleDeleteDevice(device.device_id)}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Create/Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="xl" boxShadow="xl">
          <ModalHeader
            pb={3} pt={6} px={6} fontSize="lg" fontWeight="semibold"
            borderBottomWidth="1px" borderBottomColor="gray.100"
          >
            {selectedDevice ? "Edit Device" : "Register New Device"}
          </ModalHeader>
          <ModalCloseButton top={4} right={4} />
          <ModalBody px={6} py={5}>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired isDisabled={selectedDevice !== null}>
                <FormLabel fontSize="sm">Device ID</FormLabel>
                <Input
                  placeholder="e.g., MIC_LOBBY_001"
                  value={formData.device_id}
                  onChange={(e) => setFormData({ ...formData, device_id: e.target.value })}
                  fontFamily="mono"
                />
                {!selectedDevice && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Unique hardware identifier — cannot be changed after creation
                  </Text>
                )}
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Organization</FormLabel>
                <Select
                  placeholder="Select organization"
                  value={formData.organization_id}
                  onChange={(e) => setFormData({ ...formData, organization_id: e.target.value })}
                >
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Device will only accept logins from users in this organization
                </Text>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Description</FormLabel>
                <Input
                  placeholder="e.g., Lobby microphone device"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </FormControl>

              {selectedDevice && (
                <Alert status="info" fontSize="sm" borderRadius="md">
                  <AlertIcon />
                  Device ID cannot be changed after creation
                </Alert>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter px={6} py={4} borderTopWidth="1px" borderTopColor="gray.100" gap={2}>
            <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
            <Button
              colorScheme="blue" size="sm" px={6}
              onClick={handleSubmit}
              isDisabled={!formData.device_id || !formData.organization_id}
            >
              {selectedDevice ? "Update" : "Register"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default DeviceManagement;
